import _get from 'lodash/get';
import _noop from 'lodash/noop';
import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { throwMethodOverloadError, throwNoPlayerError, throwEngineErrorHelper } from '../utils/mediaEngineErrorHelpers';
import * as INTERNAL_ERRORS from '../constants/errors/internal';
import * as DETAILS from '../constants/errors/details';
import { PLAYER_INFO } from '../constants/events';
import { createRecoverySupervisor } from '../recovery/recovery.supervisor';

const { ERROR } = VIDEO_EVENTS;

class Engine {
  constructor() {
    this.name = 'AbstractEngine';
  }

  /**
   * Stores the engine options, must be called before `init`
   *
   * @param {Object} options
   * @param {Function} options.dispatcher receives the engine events
   * @param {Object} [options.recovery] recovery policy, see recovery.constants.js
   */
  configure(options) {
    this.stopRecovery();
    this._recovery = options.recovery ? createRecoverySupervisor(this, options.recovery, options.dispatcher) : null;
    this.options = this._recovery ? { ...options, dispatcher: this._recovery.observe } : options;
  }

  stopRecovery() {
    if (this._recovery) {
      this._recovery.stop();
    }
  }

  get isLive() {
    return Boolean(this._isLive);
  }

  /**
   * Reports the player name and version, so analytics engines don't have to hardcode them
   */
  notifyPlayerInfo() {
    this.options.dispatcher(PLAYER_INFO, {
      name: this.constructor.PLAYER_NAME,
      version: this.constructor.PLAYER_VERSION,
    });
  }

  dispatchInternalError(internalErrorType, reason, content = '') {
    let error = '';
    switch (internalErrorType) {
      case INTERNAL_ERRORS.INTERNAL_ERROR_OVERLOAD:
        error = throwMethodOverloadError({ context: this.name, reason, content });
        break;
      case INTERNAL_ERRORS.INTERNAL_ERROR_GET_PLAYER:
        error = throwNoPlayerError({ context: this.name, reason, content, detail: DETAILS.NO_PLAYER });
        break;
      default:
        error = throwEngineErrorHelper({ context: this.name, reason, content });
        break;
    }

    // eslint-disable-next-line no-console
    console.error(`[${this.name}][${internalErrorType}]: ${reason} ${content}`);

    _get(this, 'options.dispatcher', _noop)(ERROR, { error });
  }

  /**
   * Reset the engine
   * @returns {Promise}
   */
  reset() {
    return Promise.resolve();
  }

  release() {
    return this.reset();
  }

  setSource(source) {
    return this.start(source);
  }
}

export default Engine;
